import React, {useState, useRef, useEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {ChevronDown, Search, X} from 'lucide-react';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  searchText?: string;
}

export interface SearchableDropdownProps {
  options: SearchableDropdownOption[];
  value: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  loadingMessage: string;
  isLoading?: boolean;
  onChange: (value: string) => void;
  onSearch?: (searchTerm: string) => void;
  renderOption?: (option: SearchableDropdownOption) => React.ReactNode;
  renderSelected?: (option: SearchableDropdownOption) => React.ReactNode;
  className?: string;
  disabled?: boolean;
  error?: string;
}

/**
 * Type-to-filter combobox for long option lists. The dropdown panel is rendered
 * through a portal (z-[9999]) so it overlays page content — including the Mapbox
 * map — without being clipped by an `overflow-hidden`/transformed ancestor.
 *
 * Provide `onSearch` to drive server-side search (debounced 300ms here); omit it
 * for purely client-side filtering over a preloaded `options` array.
 */
const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  loadingMessage,
  isLoading = false,
  onChange,
  onSearch,
  renderOption,
  renderSelected,
  className = '',
  disabled = false,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({top: 0, left: 0, width: 0});
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The dropdown panel is portaled to document.body, i.e. OUTSIDE dropdownRef,
  // so the click-outside handler needs its own ref to recognise clicks on the
  // options/search input as "inside" — otherwise mousedown closes the panel
  // before an option's click can register and selection silently fails.
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.searchText && option.searchText.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  }, [options, searchTerm]);

  // Handle search with debounce
  useEffect(() => {
    if (onSearch) {
      const timeoutId = setTimeout(() => {
        onSearch(searchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, onSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = dropdownRef.current?.contains(target);
      const insidePanel = panelRef.current?.contains(target);
      if (!insideTrigger && !insidePanel) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 320; // max-h-80 = 320px

      // Check if dropdown would go below viewport
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top = rect.bottom + window.scrollY;

      // If not enough space below, position above the input
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight;
      }

      setDropdownPosition({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);

  const handleOptionClick = useCallback((optionValue: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (disabled) {return;}
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [disabled, isOpen]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  }, [onChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-4 py-3 border rounded-lg text-left flex items-center justify-between
          transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30
          ${disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-brand-500 bg-white hover:border-gray-400'
          }
        `}
      >
        <div className="flex-1 min-w-0">
          {selectedOption ? (
            renderSelected ? (
              renderSelected(selectedOption)
            ) : (
              <span className="text-gray-900 truncate">{selectedOption.label}</span>
            )
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            } ${disabled ? 'text-gray-300' : ''}`}
          />
        </div>
      </button>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown Menu - Using Portal for better z-index handling */}
      {isOpen && !disabled && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998] bg-transparent"
            onClick={() => {
              setIsOpen(false);
              setSearchTerm('');
            }}
          />
          {/* Dropdown */}
          <div
            ref={panelRef}
            className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl max-h-80 overflow-hidden min-w-[200px]"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500 text-sm"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                {loadingMessage}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(option.value, e);
                  }}
                  className={`
                    w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50
                    focus:outline-none transition-colors cursor-pointer
                    ${option.value === value
                      ? 'bg-brand-50 text-brand-800'
                      : 'text-gray-900'
                    }
                  `}
                  style={{pointerEvents: 'auto', zIndex: 10000}}
                >
                  {renderOption ? renderOption(option) : option.label}
                </button>
              ))
            )}
          </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default SearchableDropdown;
